import { useState, useEffect, useRef } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { useToast } from "@/hooks/use-toast"
import { AIGenerateButton } from "./AIGenerateButton"
import { BasicDetails } from "./form-sections/BasicDetails"
import { Requirements } from "./form-sections/Requirements"
import { Logistics } from "./form-sections/Logistics"
import { VolunteerOutput } from "./form-sections/VolunteerOutput"
import { ImageUpload } from "./form-sections/ImageUpload"
import { AdditionalDetailsForm } from "@/components/events/create/AdditionalDetailsForm"
import { LoadingDialog } from "@/components/ui/loading-dialog"
import { 
  catalogFormSchema, 
  type Opportunity, 
  type CatalogFormData,
  arrayToString,
  stringToArray
} from "./catalog-form-schema"

const DEFAULT_WHATS_INCLUDED = "All necessary equipment and materials will be provided."
const DEFAULT_FAQS = `Q: What should I wear?\nA: Wear comfortable clothing and shoes suitable for the activity.\n\nQ: What should I bring?\nA: Please bring water and any personal items you may need.\n\nQ: Is training provided?\nA: Yes, all necessary training will be provided on-site.`
const DEFAULT_WAIVER_TEXT = "I hereby release and hold harmless the organization, its officers, directors, employees, and agents from any and all liability for any damages or injuries that I may suffer as a result of my participation in this opportunity."
const DEFAULT_TERMS_AND_CONDITIONS = "By participating in this opportunity, I acknowledge and agree to:\n1. Follow all safety guidelines and instructions\n2. Arrive on time and stay for the duration of the event\n3. Notify the organizers in advance if unable to attend\n4. Maintain professional conduct throughout the event"

interface CatalogDetailsProps {
  opportunity: Opportunity | null
  catalogData: any
  onDataChange: (data: any) => void
  onNext: () => void
  onBack: () => void
  session: any
  profile: any
  triggerAIGenerate?: boolean
}

export function CatalogDetails({
  opportunity,
  catalogData,
  onDataChange,
  onNext,
  onBack,
  session,
  profile,
  triggerAIGenerate = false
}: CatalogDetailsProps) {
  const { toast } = useToast()
  const [searchParams] = useSearchParams()
  const [isGenerating, setIsGenerating] = useState(false)
  const hasTriggeredAIRef = useRef(false)
  const opportunityIdRef = useRef<string | null>(null)

  // Trigger AI generation when the component mounts if triggerAIGenerate is true
  useEffect(() => {
    // Skip if already triggered for this opportunity or if not requested
    if (!triggerAIGenerate || !opportunity || hasTriggeredAIRef.current) {
      return;
    }
    
    // Skip if we've already processed this opportunity
    if (opportunityIdRef.current === opportunity.id) {
      return;
    }
    
    // Mark this opportunity as processed
    opportunityIdRef.current = opportunity.id;
    hasTriggeredAIRef.current = true;
    
    console.log("AI generation triggered for opportunity:", opportunity.id);
    
    // Small delay to ensure component is fully mounted
    setTimeout(() => {
      handleAIGenerate();
    }, 100);
  }, [triggerAIGenerate, opportunity]);

  const form = useForm<CatalogFormData>({
    resolver: zodResolver(catalogFormSchema),
    defaultValues: {
      title: catalogData?.title || opportunity?.name || "",
      description: catalogData?.description || "",
      prerequisites: Array.isArray(catalogData?.prerequisites) 
        ? arrayToString(catalogData.prerequisites)
        : catalogData?.prerequisites || "No specific prerequisites required.",
      timeline: catalogData?.timeline || ["Arrive 15 minutes before start time", "Event briefing and safety instructions", "Main activity", "Wrap-up and feedback"],
      eventCost: catalogData?.eventCost || 0,
      minVolunteers: catalogData?.minVolunteers || 1,
      volunteerOutput: catalogData?.volunteerOutput || "Volunteers will gain valuable experience while making a positive impact.",
      images: catalogData?.images || [],
      format: catalogData?.format || opportunity?.format || "",
      duration: catalogData?.duration || opportunity?.duration || "",
      format_details: catalogData?.format_details || "",
      whats_included: catalogData?.whats_included || DEFAULT_WHATS_INCLUDED,
      faqs: catalogData?.faqs || DEFAULT_FAQS,
      waiver_forms: catalogData?.waiver_forms || DEFAULT_WAIVER_TEXT,
      terms_and_conditions: catalogData?.terms_and_conditions || DEFAULT_TERMS_AND_CONDITIONS,
      cancellation_policy: catalogData?.cancellation_policy || "non_refundable",
      cancellation_terms: catalogData?.cancellation_terms || "No refunds will be provided for this opportunity.",
      host_included: catalogData?.host_included || false,
    },
  })

  const formState = form.formState
  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      console.log('Form validation errors:', formState.errors)
    }
  }, [formState])

  const onSubmit = (data: CatalogFormData) => {
    const formattedData = {
      ...data,
      prerequisites: stringToArray(data.prerequisites),
    }
    onDataChange(formattedData)
    onNext()
  }

  const handleAIGenerate = async () => {
    if (!opportunity) {
      toast({
        title: "Error",
        description: "Please select an opportunity first",
        variant: "destructive",
      })
      return
    }

    // Prevent duplicate calls
    if (isGenerating) {
      console.log("Generation already in progress, skipping");
      return;
    }
    
    console.log("Starting AI generation");
    setIsGenerating(true)
    try {
      const token = session.access_token;
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/catalog/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          opportunity_id: opportunity.id
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()).data;

      console.log("API response data:", data);

      // Set all the form values from the API response
      form.setValue('title', data.title)
      form.setValue('description', data.description)
      form.setValue('prerequisites', arrayToString(data.prerequisites))
      
      // Handle timeline field properly
      if (data.timeline) {
        console.log("Timeline from API:", data.timeline);
        if (Array.isArray(data.timeline)) {
          console.log("Timeline is an array, setting directly");
          // The form expects timeline as an array, so we set it directly
          form.setValue('timeline', data.timeline);
        } else if (typeof data.timeline === 'string') {
          console.log("Timeline is a string, parsing to array");
          // Parse the string into an array by splitting on newlines
          const timelineArray = data.timeline.split('\n')
            .map(line => line.trim())
            .filter(Boolean);
          form.setValue('timeline', timelineArray);
        } else {
          console.log("Timeline is in an unexpected format:", typeof data.timeline);
        }
      } else {
        console.log("No timeline in API response");
      }
      
      // For volunteerOutput, use output field if available
      form.setValue('volunteerOutput', "".concat(data.output || data.volunteerOutput || "", " ", data.impact || "", " ", data.outcome || ""))
      
      form.setValue('eventCost', data.eventCost)
      form.setValue('minVolunteers', data.minVolunteers)
      form.setValue('maxVolunteers', data.maxVolunteers)
      
      // If there's a generated image URL, add it to the images array
      if (data.imageUrl) {
        const currentImages = form.getValues('images') || [];
        form.setValue('images', [...currentImages, data.imageUrl]);
      }
      
      // Combine impact, output, and outcome for the volunteer impact field
      let volunteerImpact = '';
      
      if (data.impact) {
        volunteerImpact += data.impact;
      }
      
      if (data.output) {
        if (volunteerImpact) volunteerImpact += '\n\n';
        volunteerImpact += `Output: ${data.output}`;
      }
      
      if (data.outcome) {
        if (volunteerImpact) volunteerImpact += '\n\n';
        volunteerImpact += `Outcome: ${data.outcome}`;
      }
      
      if (volunteerImpact) {
        form.setValue('volunteerOutput', volunteerImpact);
      }
      
      toast({
        title: "Content generated!",
        description: "AI has filled in the details and generated an image. Feel free to edit them.",
      })
    } catch (error) {
      console.error('AI generation error:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to generate content. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Catalog Details</h3>
          <AIGenerateButton isGenerating={isGenerating} onClick={handleAIGenerate} />
        </div>
        <BasicDetails form={form} />
        <Requirements form={form} />
        <VolunteerOutput form={form} />
        <Logistics form={form} />
        <ImageUpload form={form} />
        <AdditionalDetailsForm form={form} />
        
        <div className="flex justify-between mt-8">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button type="submit">Next</Button>
        </div>
      </form>
      
      {/* Loading Dialog for AI Generation */}
      <LoadingDialog 
        open={isGenerating} 
        title="AI Generation in Progress" 
        description="Please wait while we generate catalog content based on your opportunity..." 
      />
    </Form>
  )
}
